import React, { useState, useEffect, useRef, useMemo } from 'react'
import { FaTimes, FaChevronDown } from 'react-icons/fa'
import { SearchFilters } from './SearchFiltersContext'

interface MySelectProps {
  label: string
  options: {
    value: string | boolean
    label: string | JSX.Element
    displayText?: string
  }[]
  value: string
  onChange: (value: string) => void
  error?: string
  className?: string
  required?: boolean
  emptyFieldError?: string
  initialValue?: string
  id?: string
  readOnly?: boolean
  icon?: React.ReactNode
  handleReset?: () => void
  variant?: 'default' | 'outlined' | 'filled' | 'establishmentFilter' | 'mobile'
  isInvalid?: boolean
  autoComplete?: string
  hideLabel?: boolean
  selectedLabel?: React.ReactNode
  showNonMatchingOptions?: boolean
}

const MySelect: React.FC<MySelectProps> = ({
  className = '',
  error,
  label,
  options,
  onChange,
  value = '',
  readOnly,
  id,
  icon,
  handleReset,
  variant = 'default',
  isInvalid = false,
  required = false,
  selectedLabel,
  hideLabel = false,
  showNonMatchingOptions = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [selectedValue, setSelectedValue] = useState(value)
  const [isOpen, setIsOpen] = useState(false)
  const [, setIsFocused] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [filteredOptions, setFilteredOptions] = useState(options)
  const inputRef = useRef<HTMLInputElement>(null)
  const uniqueId = useRef(`select-${Math.random().toString(36).substr(2, 9)}`)
  const normalizeString = (str: string) =>
    str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

  const [, setFilters] = useState<SearchFilters>({
    imLookingFor: [],
    orientation: [],
    iLiveIn: [],
    distance: [],
  })

  const getDisplayText = (label: string | JSX.Element): string => {
    if (typeof label === 'string') return label
    return options.find(opt => opt.value === value)?.displayText || ''
  }

  useEffect(() => {
    const selectedOption = options.find(option => option.value === value)
    if (selectedOption) {
      setInputValue(getDisplayText(selectedOption.label))
      setSelectedValue(value)
    }
  }, [value, options])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const filterAndSortOptions = () => {
      const searchText = normalizeString(inputValue) // Normalise la recherche
      const matchingOptions = options.filter(option =>
        normalizeString(getDisplayText(option.label)).includes(searchText),
      )

      const nonMatchingOptions = options.filter(
        option =>
          !normalizeString(getDisplayText(option.label)).includes(searchText),
      )

      const sortedMatching = matchingOptions.sort((a, b) => {
        const aText = normalizeString(getDisplayText(a.label))
        const bText = normalizeString(getDisplayText(b.label))

        const aStartsWith = aText.startsWith(searchText)
        const bStartsWith = bText.startsWith(searchText)

        if (aStartsWith && !bStartsWith) return -1
        if (!aStartsWith && bStartsWith) return 1
        return aText.localeCompare(bText)
      })

      setFilteredOptions(
        showNonMatchingOptions
          ? [...sortedMatching, ...nonMatchingOptions]
          : sortedMatching,
      )
    }

    filterAndSortOptions()
  }, [inputValue, options, showNonMatchingOptions])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return
    const newValue = event.target.value
    setInputValue(newValue)
    setIsOpen(true)
    if (newValue === '') {
      setSelectedValue('')
      onChange('')
    }
  }

  const handleOptionClick = (optionValue: string | boolean) => {
    const selectedOption = options.find(option => option.value === optionValue)
    if (selectedOption) {
      setInputValue(getDisplayText(selectedOption.label))
      setSelectedValue(optionValue.toString())
      onChange(optionValue.toString())
    }
    setIsOpen(false)
  }

  const handleBlur = () => {
    setIsFocused(false)
    const selectedOption = options.find(option => option.label === inputValue)
    if (!selectedOption) {
      setInputValue('')
      setSelectedValue('')
      onChange('')
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const getBackgroundColor = () => {
    if (className.includes('!bg-white')) {
      return 'bg-white hover:bg-white rounded-md'
    }

    if (variant === 'mobile') {
      return 'bg-grisclair'
    }

    if (isInvalid || error) return 'bg-redlight rounded-md'
    if (required && !selectedValue) return 'bg-redlight rounded-md'
    if (selectedValue) return 'bg-grisclair hover:bg-grisclair2 rounded-md'
    return 'bg-grisclair hover:bg-grisclair2 rounded-md'
  }

  const filteredAndSortedOptions = useMemo(() => {
    return options.sort((a, b) => {
      if (a.value === value) return -1
      if (b.value === value) return 1

      const aText = getDisplayText(a.label).toLowerCase()
      const bText = getDisplayText(b.label).toLowerCase()
      const searchText = inputValue.toLowerCase()

      const aIncludes = aText.includes(searchText)
      const bIncludes = bText.includes(searchText)

      if (aIncludes && !bIncludes) return -1
      if (!aIncludes && bIncludes) return 1

      return aText.localeCompare(bText)
    })
  }, [options, inputValue, value])

  const updateLocalStorage = (filters: any) => {
    const cleanedFilters = {
      ...filters,
      imLookingFor: filters.imLookingFor.filter((item: string) => item !== ''),
      orientation: filters.orientation.filter((item: string) => item !== ''),
      iLiveIn: filters.iLiveIn.filter((item: string) => item !== ''),
      distance: filters.distance.filter((item: string) => item !== ''),
    }
    localStorage.setItem('searchFilters', JSON.stringify(cleanedFilters))
  }

  const handleClear = () => {
    setInputValue('')
    setSelectedValue('')
    onChange('')
    handleReset && handleReset()
    inputRef.current?.focus()

    const newFilters = {
      imLookingFor: [],
      orientation: [],
      iLiveIn: [],
      distance: [],
    }
    setFilters(newFilters)
    updateLocalStorage(newFilters)
  }

  const toggleDropdown = () => setIsOpen(!isOpen)

  const hasSelectedOption = value !== ''

  return (
    <div
      ref={containerRef}
      className={`flex flex-col w-full relative bg-grisclair hover:bg-grisclair2 rounded-md hover:rounded-md cursor-pointer ${className}`}
    >
      <div
        className={`relative flex w-full h-40px rounded-md hover:rounded-md cursor-pointer ${
          variant === 'outlined' || variant === 'mobile'
            ? 'border-0 h-full rounded-md bg-grisclair'
            : ''
        } ${isInvalid ? 'border-redpink' : ''} ${getBackgroundColor()}`}
      >
        {(!hideLabel || (!inputValue && !selectedValue)) && (
          <>
            <label
              htmlFor={id || uniqueId.current}
              className={`transform transition-all duration-300 z-0 cursor-pointer ${
                inputValue || selectedValue
                  ? 'absolute -translate-y-1/2 -translate-x-1/2 left-1/2 ml-0 w-auto font-semibold  text-xs text-anthracite/60  rounded-t-md px-2 py-0 -z-10'
                  : `absolute inset-0 flex items-center ${
                      icon ? 'pl-10' : 'pl-2'
                    } text-anthracite/40 text-sm`
              } ${isInvalid ? 'text-gray-400' : ''} ${
                variant === 'outlined' && (inputValue || selectedValue)
                  ? 'opacity-0'
                  : ''
              }`}
              onClick={() => {
                if (!inputValue && !selectedValue) {
                  setIsOpen(true)
                  inputRef.current?.focus()
                }
              }}
            >
              {label}
              {required && <span className='ml-1'>*</span>}
            </label>
          </>
        )}
        {icon && (
          <div className='absolute left-0 top-0 bottom-0 flex items-center pl-2 text-anthracite bg-transparent rounded-l-md'>
            {icon}
          </div>
        )}
        <div
          className={`w-full  flex items-center ${
            variant === 'mobile' ? 'px-4' : icon ? 'pl-10 pr-8' : 'px-2'
          }`}
        >
          {selectedValue && selectedLabel ? (
            <div className='flex-1 flex items-center'>{selectedLabel}</div>
          ) : (
            <input
              id={id || uniqueId.current}
              ref={inputRef}
              type='text'
              className={`w-full bg-transparent text-sm border-none outline-none transition-all duration-300 ${
                inputValue || selectedValue ? 'has-value' : ''
              }`}
              value={inputValue}
              onChange={handleInputChange}
              onClick={() => setIsOpen(true)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              readOnly={readOnly}
              autoComplete='off'
            />
          )}
        </div>
        {hasSelectedOption ? (
          <button
            type='button'
            className='absolute right-2 top-1/2 transform -translate-y-1/2'
            onClick={handleClear}
            aria-label='Clear selection'
          >
            <FaTimes className='text-gray-700 hover:text-red-500' />
          </button>
        ) : (
          <button
            type='button'
            className='absolute right-2 top-1/2 transform -translate-y-1/2'
            onClick={toggleDropdown}
            aria-label={isOpen ? 'Close options' : 'Open options'}
          >
            <FaChevronDown className='text-gray-700 hover:text-redpink' />
          </button>
        )}
      </div>
      {isOpen && filteredOptions.length > 0 && (
        <ul
          className={`absolute w-full max-h-60 overflow-auto bg-anthracite text-white rounded-md shadow-[0_0_8px_0] shadow-black/100 z-50
          scrollbar-thin scrollbar-thumb-redpink scrollbar-track-anthraciteLight 
          hover:scrollbar-thumb-redpink/80
          [&::-webkit-scrollbar]:w-2
          [&::-webkit-scrollbar-track]:bg-anthraciteLight/20
          [&::-webkit-scrollbar-thumb]:bg-redpink
          [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-track]:rounded-full
          [&::-webkit-scrollbar-thumb]:hover:bg-redpink/80
          ${variant === 'mobile' ? 'top-12' : 'top-11'}`}
        >
          {filteredOptions.map((option, index) => (
            <li
              key={`${option.value}-${index}`}
              className={`px-4 border-b border-anthraciteLight text-sm py-1 cursor-pointer hover:bg-grisfonce hover:pl-5 transition-all ${
                option.value === value ? 'bg-redpink text-white' : ''
              }`}
              onMouseDown={() => handleOptionClick(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default MySelect
